import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';
import { Pill, STATUS_COLORS, ValueCell } from './BidTrackerPage';
import { mergePreview, filterMergeCandidates } from '../../utils/bidMerge';
import { fmtDeadlineWithTime } from '../../utils/bidValue';

// Merge a duplicate bid into this one.
//
// The same tender often arrives twice: once from the portal notice, once from
// an email, under two references. POST crm/bids/:id/merge keeps one row (the
// survivor), fills its blank fields from the other, appends the other's notes
// under a "Merged from" line and moves its platforms, documents, hardware,
// status history and comments across. The duplicate stays on file marked merged, so nothing
// is lost; the list simply stops showing it.
//
// By default the bid this page is open on survives. "Keep AV-BD-1070 instead"
// swaps the two, for when the duplicate turns out to be the better record.
export default function BidMergeDialog({ bid, onClose, onMerged }) {
  const [bids, setBids] = useState([]);
  const [loadingBids, setLoadingBids] = useState(true);
  const [query, setQuery] = useState('');
  const [otherId, setOtherId] = useState(null);
  const [keepOther, setKeepOther] = useState(false);
  const [counts, setCounts] = useState(null);   // { documents, hardware, history, comments } for the duplicate
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('crm/bids')
      .then(d => setBids(d?.rows || []))
      .catch(e => setErr(e?.message || 'Could not load the bid list.'))
      .finally(() => setLoadingBids(false));
  }, []);

  const other = bids.find(b => b.id === otherId) || null;
  const survivor = keepOther ? other : bid;
  const duplicate = keepOther ? bid : other;
  const candidates = filterMergeCandidates(bids, bid, query).slice(0, 12);

  // Documents, hardware, history rows and comments live in their own tables, so the
  // duplicate's counts come from the API once it is chosen.
  useEffect(() => {
    if (!duplicate) { setCounts(null); return undefined; }
    let alive = true;
    setCounts(null);
    Promise.all([
      api.get(`crm/bids/${duplicate.id}/documents`).catch(() => []),
      api.get(`crm/bids/${duplicate.id}/history`).catch(() => ({ rows: [] })),
      api.get('commentary', { entity_type: 'bid', entity_id: duplicate.id }).catch(() => []),
      api.get(`crm/bids/${duplicate.id}/hardware`).catch(() => ({ rows: [] })),
    ]).then(([docs, hist, comments, hardware]) => {
      if (!alive) return;
      setCounts({
        documents: Array.isArray(docs) ? docs.length : 0,
        hardware: (hardware?.rows || []).length,
        history: (hist?.rows || []).length,
        comments: (Array.isArray(comments) ? comments : (comments?.rows || [])).length,
      });
    });
    return () => { alive = false; };
  }, [duplicate?.id]);

  const preview = survivor && duplicate ? mergePreview(survivor, duplicate) : null;

  const merge = async () => {
    if (!survivor || !duplicate) return;
    setBusy(true); setErr('');
    try {
      await api.post(`crm/bids/${survivor.id}/merge`, { duplicate_id: duplicate.id });
      onMerged(survivor.id);
    } catch (e) {
      // The API's refusal (already merged, same bid) belongs here in the
      // dialog, next to the button that was pressed.
      setErr(e?.message || 'Could not merge these bids.');
    } finally { setBusy(false); }
  };

  const plural = (n, word, many) => `${n} ${n === 1 ? word : (many || `${word}s`)}`;
  const bidLine = (b) => (
    <span className="text-[10px]">
      <span className="font-mono font-bold text-navy">{b.reference || `#${b.id}`}</span>{' '}
      <span className="font-semibold">{b.opportunity}</span>
      <span className="text-gray-500">{b.buyer ? ` · ${b.buyer}` : ''}</span>
    </span>
  );

  return (
    <Modal title="Merge a duplicate into this bid" onClose={onClose} wide
      footer={<>
        {err && <span role="alert" className="text-[9px] font-semibold text-red-600 mr-auto">{err}</span>}
        <button onClick={onClose} className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={merge} disabled={busy || !survivor || !duplicate}
          className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-60">
          {busy ? 'Merging…' : survivor ? `Merge into ${survivor.reference || `#${survivor.id}`}` : 'Merge'}
        </button>
      </>}>
      <div className="space-y-3 text-[10px]">
        <div className="text-gray-600">
          Pick the bid that duplicates this one. The survivor keeps its own details, takes the other's where its own are blank, and inherits the other's platforms, documents, history and comments. The duplicate is kept on file, marked as merged, and disappears from the list.
        </div>

        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Find the duplicate</label>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Reference, opportunity or buyer" autoFocus
            className="form-input w-full text-[10px]" />
          {loadingBids ? (
            <div className="text-gray-400 mt-1">Loading bids…</div>
          ) : candidates.length === 0 ? (
            <div className="text-gray-400 mt-1">No other bid matches.</div>
          ) : (
            <div className="border border-gray-200 rounded mt-1 divide-y divide-gray-100 max-h-48 overflow-y-auto">
              {candidates.map(b => (
                <button key={b.id} type="button" onClick={() => { setOtherId(b.id); setKeepOther(false); setErr(''); }}
                  className={`w-full text-left px-2 py-1.5 flex items-center justify-between gap-2 hover:bg-gray-50 ${otherId === b.id ? 'bg-navy/5' : ''}`}>
                  <span className="min-w-0 truncate">{bidLine(b)}</span>
                  <span className="flex items-center gap-2 flex-shrink-0">
                    <Pill text={b.status} palette={STATUS_COLORS} />
                    <span className="text-gray-500 text-[9px]"><ValueCell b={b} size={9} /></span>
                    {otherId === b.id && <span className="text-[9px] font-bold text-navy">Selected</span>}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {other && preview && (
          <div className="border border-gray-200 rounded p-2.5 space-y-2 bg-gray-50">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <div className="text-[8px] font-bold text-gray-500 uppercase">Survivor</div>
                {bidLine(survivor)}
                <div className="text-[8px] font-bold text-gray-500 uppercase mt-1.5">Merged away</div>
                {bidLine(duplicate)}
              </div>
              <button type="button" onClick={() => { setKeepOther(k => !k); setErr(''); }}
                className="text-[9px] font-semibold text-navy underline hover:no-underline">
                Keep {duplicate.reference || `#${duplicate.id}`} instead
              </button>
            </div>

            <div>
              <div className="text-[8px] font-bold text-gray-500 uppercase mb-0.5">Blank fields filled from {duplicate.reference || `#${duplicate.id}`}</div>
              {preview.fills.length === 0 ? (
                <div className="text-gray-500">None. {survivor.reference || 'The survivor'} already has every field the duplicate carries.</div>
              ) : (
                <ul className="list-disc pl-4 space-y-0.5">
                  {preview.fills.map(f => <li key={f.key}><span className="font-semibold">{f.label}:</span> {f.value}</li>)}
                </ul>
              )}
              {preview.notesAppended && <div className="text-gray-600 mt-1">Notes: appended under a "Merged from {duplicate.reference || `#${duplicate.id}`}" line.</div>}
              {survivor.deadline && duplicate.deadline && survivor.deadline !== duplicate.deadline && (
                <div className="text-amber-700 mt-1">Both bids carry a deadline; the survivor keeps {fmtDeadlineWithTime(survivor.deadline, survivor.deadline_time)}.</div>
              )}
            </div>

            <div>
              <div className="text-[8px] font-bold text-gray-500 uppercase mb-0.5">Moved to {survivor.reference || `#${survivor.id}`}</div>
              {counts ? (
                <div className="text-gray-700">
                  {[plural(preview.platforms, 'platform'), plural(counts.documents, 'document'), plural(counts.hardware, 'hardware quote or deal', 'hardware quotes or deals'), plural(counts.history, 'status history row'), plural(counts.comments, 'comment')].join(', ')}.
                </div>
              ) : <div className="text-gray-400">Counting…</div>}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
